// Per-backend-source notification schema shared by portfolios, headline
// queries, and tags watchlists (user pref-service).
// Shape: { [backend source]: enabled methods }.

/**
 * Notification backend sources. Only "newsquawk-headlines" exists today; more
 * will be added later. Distinct from the Firehose `BackendSource` type — the
 * two will be merged at a later point.
 */
export type NotificationBackendSource = "newsquawk-headlines";

export type NotificationMethod = "email" | "popup" | "sound";

/**
 * Backend source → enabled notification methods. The backend may return
 * sources beyond `NotificationBackendSource`; they must be preserved when
 * round-tripping (PATCH replaces the whole object), hence the open key type.
 */
export type FilterNotifications = Record<string, NotificationMethod[]>;

export const PRIMARY_NOTIFICATION_BACKEND_SOURCE: NotificationBackendSource =
  "newsquawk-headlines";

/** Backend sources the UI renders controls for. */
export const NOTIFICATION_BACKEND_SOURCES: NotificationBackendSource[] = [
  PRIMARY_NOTIFICATION_BACKEND_SOURCE,
];

export const NOTIFICATION_BACKEND_SOURCE_LABELS: Record<
  NotificationBackendSource,
  string
> = {
  "newsquawk-headlines": "Newsquawk Headlines",
};

/** Default for new filters: primary backend source present, nothing enabled. */
export const emptyNotifications = (): FilterNotifications => ({
  [PRIMARY_NOTIFICATION_BACKEND_SOURCE]: [],
});

export const isNotificationMethodEnabled = (
  notifications: FilterNotifications | undefined,
  method: NotificationMethod,
  source: string = PRIMARY_NOTIFICATION_BACKEND_SOURCE
): boolean => notifications?.[source]?.includes(method) ?? false;

/**
 * Returns a new map with `method` switched on/off for `source`. Other backend
 * sources are carried over untouched so loaded filters round-trip intact.
 */
export const withNotificationMethod = (
  notifications: FilterNotifications | undefined,
  method: NotificationMethod,
  enabled: boolean,
  source: string = PRIMARY_NOTIFICATION_BACKEND_SOURCE
): FilterNotifications => {
  const base = notifications ?? emptyNotifications();
  const current = base[source] ?? [];
  const next = enabled
    ? current.includes(method)
      ? current
      : [...current, method]
    : current.filter((m) => m !== method);
  return { ...base, [source]: next };
};

export const hasAnyNotificationEnabled = (
  notifications: FilterNotifications | undefined
): boolean =>
  Object.values(notifications ?? {}).some((methods) => methods.length > 0);
